/**
 * useStudentSamLayer — Sam sur l'activité, hors live : sa tuile (`samTile`) et sa conversation
 * (`samLayer`) pour `<StudentActivityPage>`. Il n'y a pas de page « session Sam » : c'est la page
 * d'activité, Sam en plus.
 *
 * Le moteur est celui de l'ancien `SamSessionScreen`, déplacé (`engine/activity/sam`) : le contexte de
 * Sam (5 min de cache), la conversation (un message en panne se renvoie), les deux relances (5 s,
 * 3 min) tant que l'élève est devant l'écran et n'a pas rendu sa copie, le « Où j'en suis » en photo
 * (l'appareil photo de l'app, sans le scanner). L'aperçu de la tuile dit le dernier mot de Sam pendant
 * 7 s, feuille fermée et élève présent. La copie rendue, Sam se range.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useIsFocused } from "@react-navigation/native"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AppState } from "react-native"

import {
    SamConversation,
    SamFloatingTile,
    SamSheet,
    type SamSheetDetent,
} from "../../../../../../design-system/student"
import { formIds } from "../../../../../../data/constants/formConstants"
import { useSamContext } from "../../../../../../engine/activity/sam/useSamContext"
import { useSamConversation } from "../../../../../../engine/activity/sam/useSamConversation"
import { useSamProactivity } from "../../../../../../engine/activity/sam/useSamProactivity"
import { useSamWorkCheck } from "../../../../../../engine/activity/sam/useSamWorkCheck"
import {
    SAM_TEXTS,
    samConversationModel,
    samPeek,
    type SamAction,
    type SamThreadMessage,
} from "../../../../view-models/student/Activity/studentSamViewModel"
import { useMediaPickerAdapter } from "../../../hooks/useMediaPickerAdapter"
import { samSessionStartedProps } from "./activityAnalytics"

/** L'aperçu reste 7 s (`SamSessionScreen`). */
const PEEK_MS = 7000
/** La lecture d'une photo qui dure : Sam le dit au-delà de 9 s (`ANALYSIS_REASSURE_AFTER_MS`). */
const WORK_CHECK_LATE_MS = 9000

function useStudentPresent(): boolean {
    const focused = useIsFocused()
    const [foreground, setForeground] = useState(AppState.currentState !== "background")
    useEffect(() => {
        // Sur le web, `AppState` suit `visibilitychange` : le changement d'onglet est couvert.
        const sub = AppState.addEventListener("change", (next) => setForeground(next === "active"))
        return () => sub.remove()
    }, [])
    return focused && foreground
}

export function useStudentSamLayer(activityId: number | null, submitted: boolean, entryPoint?: string | null) {
    const present = useStudentPresent()
    const { context, hasError } = useSamContext(activityId, { offlineDialog: false })
    const conversation = useSamConversation(activityId, { offlineDialog: false })
    const [sheetOpen, setSheetOpen] = useState(false)
    // La feuille s'agrandit et se réduit d'un geste, comme l'ancienne (`SamConversationSheet`) ; elle
    // se rouvre à sa hauteur basse.
    const [detent, setDetent] = useState<SamSheetDetent>("default")
    const [draft, setDraft] = useState("")
    const openSheet = useCallback(() => {
        setDetent("default")
        setSheetOpen(true)
    }, [])
    const closeSheet = useCallback(() => setSheetOpen(false), [])

    const camera = useMediaPickerAdapter({ formId: formIds.SAM_WORK_CHECK, geniusScan: false })
    const workCheck = useSamWorkCheck(activityId, openSheet, camera.launchCamera, { offlineDialog: false })

    useSamProactivity({ activityId, enabled: !submitted && present && context != null, sheetOpen })

    // La copie rendue : la feuille se ferme, Sam se tait.
    useEffect(() => {
        if (submitted) setSheetOpen(false)
    }, [submitted])

    const messages = conversation.messages as SamThreadMessage[]
    const lastSam = samPeek(messages)
    const lastId = messages[messages.length - 1]?.id ?? null
    const [peek, setPeek] = useState<string | null>(null)
    useEffect(() => {
        if (sheetOpen || !present || lastSam == null) {
            setPeek(null)
            return
        }
        setPeek(lastSam)
        const timer = setTimeout(() => setPeek(null), PEEK_MS)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheetOpen, present, lastId])

    const [late, setLate] = useState(false)
    useEffect(() => {
        setLate(false)
        if (!workCheck.isUploading) return
        const timer = setTimeout(() => setLate(true), WORK_CHECK_LATE_MS)
        return () => clearTimeout(timer)
    }, [workCheck.isUploading])

    // Les mesures de la séance avec Sam (`SamSessionScreen`).
    const startedAt = useRef(Date.now())
    const counted = useRef({ started: false, completed: false, messages: 0 })
    counted.current.messages = messages.length
    useEffect(() => {
        if (context == null || counted.current.started) return
        counted.current.started = true
        amplitude.track("sam.session.started", samSessionStartedProps(activityId, entryPoint, context.source_kind))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context, activityId])
    useEffect(() => {
        if (!submitted || counted.current.completed) return
        counted.current.completed = true
        amplitude.track("sam.session.completed", {
            activity_id: activityId,
            messages_count: counted.current.messages,
            duration_sec: Math.round((Date.now() - startedAt.current) / 1000),
        })
    }, [submitted, activityId])
    useEffect(
        () => () => {
            if (counted.current.completed) return
            amplitude.track("sam.session.abandoned", {
                activity_id: activityId,
                messages_count: counted.current.messages,
                time_spent_sec: Math.round((Date.now() - startedAt.current) / 1000),
            })
        },
        [activityId]
    )

    const model = useMemo(
        () =>
            samConversationModel({
                messages,
                isThinking: !!conversation.isThinking,
                degraded: !!conversation.degraded,
                capScope: conversation.capScope as "activity" | "daily" | null,
                failedMessageId: conversation.failedMessageId,
                workCheckUploading: workCheck.isUploading,
                workCheckLate: late,
            }),
        [
            messages,
            conversation.isThinking,
            conversation.degraded,
            conversation.capScope,
            conversation.failedMessageId,
            workCheck.isUploading,
            late,
        ]
    )

    const act = useCallback(
        (key: string) => {
            switch (key as SamAction) {
                case "work_check":
                    workCheck.pickFromCamera()
                    return
                case "explain":
                    conversation.sendText(SAM_TEXTS.explainMessage)
                    return
                case "hint":
                    conversation.askHint()
                    return
                case "course_reminder":
                    conversation.askCourseReminder()
                    return
            }
        },
        [conversation, workCheck]
    )

    const samTile =
        !sheetOpen && !submitted ? (
            <SamFloatingTile
                onOpen={openSheet}
                peek={peek}
                onDismissPeek={() => setPeek(null)}
                unread={messages[messages.length - 1]?.role === "sam"}
            />
        ) : undefined

    const samLayer = sheetOpen ? (
        <SamSheet onClose={closeSheet} detent={detent} onDetentChange={setDetent}>
            <SamConversation
                days={model.days}
                welcome={model.welcome}
                starters={model.starters}
                onStarter={act}
                thinking={model.thinking}
                workCheck={model.workCheck}
                cap={model.cap}
                slow={model.slow}
                quickReplies={model.quickReplies}
                onQuickReply={act}
                draft={draft}
                onChangeDraft={setDraft}
                onSend={() => {
                    if (draft.trim() === "") return
                    conversation.sendText(draft)
                    setDraft("")
                }}
                onClose={closeSheet}
                onRetryMessage={(message) => conversation.retryMessage(message.id)}
            />
        </SamSheet>
    ) : undefined

    return {
        context,
        contextError: hasError,
        samTile,
        samLayer,
        reset: conversation.reset,
    }
}
